/**
 * The portal's type and control scale — one definition, imported by every
 * screen.
 *
 * ─── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 * `labelStyle` was copy-pasted into NINE screens and `purpleBtn` into seven.
 * They were byte-identical apart from `marginBottom`, which is the good case;
 * the bad case is what happens next, when someone tunes a weight on the screen
 * they happen to be in and the portal quietly ends up with two label styles.
 * Consolidating is also what made this file's actual job — a weight pass —
 * a one-place edit instead of nine chances to disagree.
 *
 * ─── THE VALUES COME FROM THE DEMO ───────────────────────────────────────────
 * Matched to the tenant surface in `parkifycodebase/parkify-demo`
 * (`TField`, `TCard`, `TStat`, `TBtn`), because that is the artifact a prospect
 * is shown and the one this portal is measured against. The portal had been
 * running one weight step LIGHT of it almost everywhere — 700 where the demo
 * uses 800, and 400 (nothing set at all) where it uses 600 — which is what made
 * our screens read as washed out next to it even after the colour was fixed.
 *
 * ⚠️ The uppercase label is SMALLER and BOLDER than what it replaced
 * (11px/700/0.5px → 10px/800/1px). That looks backwards written down and isn't:
 * at 11px/700 with barely any tracking it read as faded body copy, which is
 * precisely what a section label must not do. Small + heavy + open tracking is
 * what makes it read as a label rather than as text someone forgot to darken.
 *
 * Action button radius is a shared tenant token. Primary and secondary actions
 * keep the demo's 9px corners; status chips remain semantic pills and use the
 * separate pill token below.
 */
import type { CSSProperties } from 'react'

/** Action buttons match the squared-off demo treatment. */
export const buttonRadius = 9

/** Semantic status/filter chips remain pills. */
export const pillRadius = 999

/** Section/field label. Small, heavy, openly tracked — see the note above. */
export const labelStyle: CSSProperties = {
  fontSize: 10, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase',
  color: 'var(--color-gray-600)', marginBottom: 4,
}

/** The `<h2>` at the top of a screen. */
export const screenTitle: CSSProperties = {
  fontSize: 23, fontWeight: 800, letterSpacing: '-0.6px', margin: 0,
}

/** A card's own heading, one step down from the screen title. */
export const cardTitle: CSSProperties = {
  fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.2px',
}

/**
 * Explanatory copy inside a card.
 *
 * `fontWeight` is set EXPLICITLY. Every one of these blocks used to omit it and
 * inherit 400, which against the demo's 600 was the single biggest reason our
 * cards looked thin — and an omission is invisible in review in a way a wrong
 * value isn't.
 */
export const cardBody: CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.55,
}

export const purpleBtn: CSSProperties = {
  padding: '8px 16px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none',
  fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
}

export const ghostBtn: CSSProperties = {
  ...purpleBtn, background: 'transparent', color: 'var(--color-gray-700)',
  border: '1.5px solid var(--color-gray-300)',
}

/** Shared modal form rhythm: one gap between field groups, with actions kept
 * visibly separate from the final explanatory copy. */
export const modalSection: CSSProperties = { marginBottom: 16 }
export const modalActions: CSSProperties = {
  display: 'flex', gap: 8, marginTop: 24, justifyContent: 'flex-end',
}

/**
 * The full-width submit on the public registration form — a phone-first target,
 * so it keeps its own size rather than inheriting the console-sized button.
 */
export const submitBtn: CSSProperties = {
  ...purpleBtn, width: '100%', padding: '14px 16px', fontSize: 15,
}

/**
 * A native `<select>`, which otherwise renders as the one piece of unstyled
 * browser chrome on an otherwise designed screen — 400 weight and pure #000,
 * next to type that is 600+ and Master Black everywhere else.
 */
export const selectStyle: CSSProperties = {
  padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)',
  background: '#fff', color: 'var(--color-black)', fontSize: 13, fontWeight: 700,
  fontFamily: 'inherit', cursor: 'pointer',
}
